import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

// Placeholder reference pairs – replace with real images once provided
const references = [
  {
    id: 1,
    label: 'Referenz 1',
    beforeSrc: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/14773cd363b7c0330b6e879acb1da558.png',
    afterSrc: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/8ca73b310622153b78943df680402884.png',
    beforeLabel: 'Veraltetes Design',
    afterLabel: 'Professionell & modern',
    afterUrl: 'https://www.die-baumexperten.de/',
  },
  {
    id: 2,
    label: 'Referenz 2',
    beforeSrc: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/d769b9886ea8b607a2e04eb650349b28.png',
    afterSrc: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/89922c57af8be093b3b4e22f01a0fa60.png',
    beforeLabel: 'Chaos ohne Struktur',
    afterLabel: 'Klar & überzeugend',
    afterUrl: 'https://tierkommunikation-buch.de/',
  },
  {
    id: 3,
    label: 'Referenz 3',
    beforeSrc: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/f70487d5abd6fc6223f282c8b3ffaa0c.png',
    afterSrc: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/4094b0aa3bcd928fc1e67cf85bb24dbe.png',
    beforeLabel: 'Wenig Raum für Gefühle',
    afterLabel: 'Wohlfühlen auf den ersten Blick',
    afterUrl: '',
  },
];

export default function Transformation() {
  const { theme } = useTheme();
  const [showRefs, setShowRefs] = useState(false);
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: beforeRef, isVisible: beforeVisible } = useScrollAnimation({ threshold: 0.15 });
  const { ref: afterRef, isVisible: afterVisible } = useScrollAnimation({ threshold: 0.15 });
  const { ref: badgeRef, isVisible: badgeVisible } = useScrollAnimation({ threshold: 0.3 });

  useEffect(() => {
    const handleOpen = () => setShowRefs(true);
    window.addEventListener('openReferenzen', handleOpen);
    return () => window.removeEventListener('openReferenzen', handleOpen);
  }, []);

  return (
    <section
      id="transformation"
      className="py-12 sm:py-20 md:py-28 px-4"
      style={{ backgroundColor: '#FFFFFF', fontFamily: theme.fonts.body }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          ref={headRef}
          className="text-left sm:text-center mb-8 sm:mb-12 md:mb-16"
          style={{
            opacity: headVisible ? 1 : 0,
            transform: headVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <span className="text-xs tracking-[0.3em] uppercase font-semibold mb-3 block" style={{ color: theme.colors.accent }}>
            Vorher / Nachher
          </span>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight"
            style={{ fontFamily: theme.fonts.heading, color: theme.colors.text }}
          >
            <span style={{
              background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Vorausgehen
            </span>
            {' '}statt{' '}
            <span style={{
              background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              mitlaufen.
            </span>
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: theme.colors.textSecondary }}>
            Der Unterschied zeigt sich in Anfragen, Vertrauen und Umsatz.
          </p>
        </div>

        {/* Visual Before / After */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">

          {/* BEFORE */}
          <div
            ref={beforeRef}
            className="relative rounded-2xl overflow-hidden group h-[220px] sm:h-[280px] md:h-[320px]"
            style={{
              opacity: beforeVisible ? 1 : 0,
              transform: beforeVisible ? 'translateX(0)' : 'translateX(-70px)',
              transition: 'opacity 0.9s ease, transform 0.9s ease',
            }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=An%20old%20CRT%20monitor%20sitting%20on%20a%20heavily%20cluttered%20dusty%20wooden%20desk%20in%20a%20dim%20storage%20room%2C%20the%20screen%20displaying%20a%20chaotic%20outdated%20website%20from%20the%20early%202000s%20with%20garish%20lime%20green%20and%20orange%20colors%2C%20tiny%20unreadable%20text%20and%20broken%20layout%2C%20stacks%20of%20yellowed%20papers%20and%20overflowing%20ring%20binders%20everywhere%20on%20desk%2C%20tangled%20cables%20and%20outdated%20beige%20keyboard%20with%20worn%20keys%2C%20absolutely%20no%20people%20present%2C%20dim%20single%20tungsten%20bulb%20casting%20harsh%20shadows%20across%20the%20mess%2C%20vintage%20neglected%20abandoned%20atmosphere%20in%20dark%20moody%20tones&width=700&height=500&seq=before-website-v4&orientation=landscape"
              alt="Veraltete Website – Vorher"
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30"></div>

            <div className="absolute top-5 left-5 flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(239,68,68,0.92)' }}>
              <i className="ri-close-circle-line text-white text-sm"></i>
              <span className="text-white text-xs font-bold tracking-wide uppercase">Vorher</span>
            </div>

            <div className="absolute top-5 right-5">
              <div className="text-6xl font-black text-white/10 leading-none select-none">✗</div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
              <p className="hidden sm:block text-white/50 text-xs uppercase tracking-widest font-semibold mb-3 sm:mb-4">Mitlaufen</p>
              <ul className="space-y-1.5 sm:space-y-3">
                {['Austauschbar', 'Keine klare Botschaft', 'Wirkt unprofessionell', 'Besucher springen ab'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <i className="ri-close-line text-red-400 text-sm flex-shrink-0"></i>
                    <span className="text-white/90 text-sm font-semibold">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Center arrow */}
          <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-2xl"
              style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)` }}
            >
              <i className="ri-arrow-right-line text-white text-2xl"></i>
            </div>
          </div>

          {/* AFTER */}
          <div
            ref={afterRef}
            className="relative rounded-2xl overflow-hidden group h-[220px] sm:h-[280px] md:h-[320px]"
            style={{
              opacity: afterVisible ? 1 : 0,
              transform: afterVisible ? 'translateX(0)' : 'translateX(70px)',
              transition: 'opacity 0.9s ease 0.15s, transform 0.9s ease 0.15s',
            }}
          >
            <img
              src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/bfc0f6d2bc5259985c89231514028138.png"
              alt="Moderne Website – Nachher"
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"></div>

            <div
              className="absolute top-5 left-5 flex items-center gap-2 px-4 py-2 rounded-full"
              style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)` }}
            >
              <i className="ri-checkbox-circle-line text-white text-sm"></i>
              <span className="text-white text-xs font-bold tracking-wide uppercase">Nachher</span>
            </div>

            <div className="absolute top-5 right-5">
              <div className="text-6xl font-black text-white/10 leading-none select-none">✓</div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
              <p className="hidden sm:block text-white/50 text-xs uppercase tracking-widest font-semibold mb-3 sm:mb-4">Vorausgehen</p>
              <ul className="space-y-1.5 sm:space-y-3">
                {['Unverwechselbar', 'Klare Botschaft', 'Schafft Vertrauen', 'Besucher werden Kunden'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <i className="ri-checkbox-circle-line text-sm flex-shrink-0" style={{ color: theme.colors.accent }}></i>
                    <span className="text-white/90 text-sm font-semibold">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom badge + Referenzen Button */}
        <div
          ref={badgeRef}
          className="flex flex-col items-center gap-5 mt-10"
          style={{
            opacity: badgeVisible ? 1 : 0,
            transform: badgeVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          {/* Referenzen Button */}
          <button
            onClick={() => setShowRefs(prev => !prev)}
            className="flex items-center gap-2.5 px-8 py-3.5 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap transition-all duration-300"
            style={{
              backgroundColor: showRefs ? theme.colors.accent : 'transparent',
              color: showRefs ? '#ffffff' : theme.colors.accent,
              border: `1.5px solid ${theme.colors.accent}`,
            }}
          >
            <i className={`ri-gallery-line text-base transition-transform duration-300 ${showRefs ? 'rotate-0' : ''}`}></i>
            Referenzen ansehen
            <i className={`ri-arrow-${showRefs ? 'up' : 'down'}-s-line text-base`}></i>
          </button>
        </div>

        {/* Expandable References Gallery */}
        <div
          className="overflow-hidden transition-all duration-700"
          style={{
            maxHeight: showRefs ? '2000px' : '0px',
            opacity: showRefs ? 1 : 0,
          }}
        >
          <div className="pt-6 sm:pt-12 space-y-6 sm:space-y-10">
            <p className="text-center text-xs tracking-[0.3em] uppercase font-semibold" style={{ color: theme.colors.accent }}>
              Echte Projekte — echte Ergebnisse
            </p>

            {references.map((ref) => (
              <div key={ref.id} className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: theme.colors.textSecondary }}>
                  {ref.label}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5 relative">

                  {/* Before */}
                  <div className="relative rounded-xl overflow-hidden group h-[160px] sm:h-[210px] md:h-[260px]">
                    <img
                      src={ref.beforeSrc}
                      alt={`${ref.label} – Vorher`}
                      className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20"></div>
                    <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(239,68,68,0.9)' }}>
                      <i className="ri-close-circle-line text-white text-xs"></i>
                      <span className="text-white text-xs font-bold uppercase tracking-wide">Vorher</span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-white font-semibold text-sm">{ref.beforeLabel}</p>
                    </div>
                  </div>

                  {/* Center arrow */}
                  <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)` }}
                    >
                      <i className="ri-arrow-right-line text-white text-lg"></i>
                    </div>
                  </div>

                  {/* After */}
                  {ref.afterUrl ? (
                    <a
                      href={ref.afterUrl}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="relative rounded-xl overflow-hidden group block cursor-pointer h-[160px] sm:h-[210px] md:h-[260px]"
                    >
                      <img
                        src={ref.afterSrc}
                        alt={`${ref.label} – Nachher`}
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent"></div>
                      <div
                        className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full"
                        style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)` }}
                      >
                        <i className="ri-checkbox-circle-line text-white text-xs"></i>
                        <span className="text-white text-xs font-bold uppercase tracking-wide">Nachher</span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                        <p className="text-white font-bold text-sm">{ref.afterLabel}</p>
                        <span className="flex items-center gap-1 text-white/70 text-xs">
                          <i className="ri-external-link-line text-sm"></i>
                          Website ansehen
                        </span>
                      </div>
                    </a>
                  ) : (
                    <div className="relative rounded-xl overflow-hidden group h-[160px] sm:h-[210px] md:h-[260px]">
                      <img
                        src={ref.afterSrc}
                        alt={`${ref.label} – Nachher`}
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent"></div>
                      <div
                        className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full"
                        style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)` }}
                      >
                        <i className="ri-checkbox-circle-line text-white text-xs"></i>
                        <span className="text-white text-xs font-bold uppercase tracking-wide">Nachher</span>
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <p className="text-white font-bold text-sm">{ref.afterLabel}</p>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            ))}

            {/* Close button */}
            <div className="flex justify-center pt-4 pb-2">
              <button
                onClick={() => setShowRefs(false)}
                className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap transition-opacity duration-200 hover:opacity-70"
                style={{ color: theme.colors.textSecondary }}
              >
                <i className="ri-arrow-up-s-line text-base"></i>
                Schließen
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
